import { apiFetch } from '../auth/api';

/** Per-deal-type closed totals (Round L-3, Daniel 2026-06-09). The API
 *  always returns all three keys so the UI doesn't need to null-guard. */
export interface TypeBucket { units: number; volume_cents: number; }
export interface TypeBreakdown {
  listing: TypeBucket;
  buyer: TypeBucket;
  auction: TypeBucket;
}

export interface AgentGoals {
  user_id: number;
  team_id: number;
  year: number;
  // Legacy fields (kept for backwards compat; new UI doesn't use them)
  annual_volume: number;
  annual_units: number;
  annual_appointments: number;
  annual_agreements: number;
  annual_listings: number;
  annual_buyers: number;
  daily_calls: number;
  daily_contacts: number;
  daily_leads: number;
  daily_appointments: number;
  daily_social_posts: number;
  // MREA-style economic model fields (the ones that matter going forward)
  unit_goal: number;
  sell_side_pct: number;
  working_weeks: number;
  sell_avg_sale_price: number;
  sell_commission_pct: number;
  sell_sold_conversion: number;
  sell_appointment_conversion: number;
  buy_avg_sale_price: number;
  buy_commission_pct: number;
  buy_sold_conversion: number;
  buy_appointment_conversion: number;
  agent_team_split: number;
  /** Per-agent listing-side split (0..1). Added in migration 0026. */
  listing_split_pct: number;
  conversations_per_appointment: number;
  track_dials: number; // 0 or 1 — show the Dials field in the daily activity logger
}

export interface TeamAgent {
  id: number;
  first_name: string;
  last_name: string;
  show_on_production: number;
  annual_volume: number | null;   // cents
  annual_units: number | null;
  annual_appointments: number | null;
  daily_calls: number | null;
  daily_contacts: number | null;
  daily_leads: number | null;
  daily_appointments: number | null;
  daily_social_posts: number | null;
  ytd_closed_units: number;
  ytd_closed_volume: number;      // cents
}

export interface TeamOverview {
  year: number;
  agents: TeamAgent[];
}

export interface DailyActivity {
  activity_date: number;
  calls: number;              // dials
  contacts_made: number;
  leads_added: number;
  appointments: number;       // legacy — kept for back-compat
  social_posts: number;
  appointments_set: number;
  appointments_held: number;
  // R11-3 (Daniel 2026-08-12): of the appointments held, how many were
  // listing appointments. Migration 0206; feeds the coach scorecard.
  listing_appointments_held: number;
  listings_signed: number;
  buyers_signed: number;
  contracts: number;
  vendor_referrals: number;
  // Build 18 (Daniel 2026-09-16): buyer showings conducted. Migration 0266.
  showings: number;
}

export interface ClosedDeal {
  id: number;
  address_street: string | null;
  address_city: string | null;
  address_state: string | null;
  sale_price: number | null;       // cents
  close_date: number | null;
  type: 'auction' | 'listing' | 'buyer';
}

export interface ActiveDeal {
  id: number;
  address_street: string | null;
  address_city: string | null;
  address_state: string | null;
  list_price: number | null;       // cents
  sale_price: number | null;       // cents
  close_date: number | null;
  contract_date: number | null;
  type: 'auction' | 'listing' | 'buyer';
  status: 'active' | 'under_contract';
  current_phase_slug: string;
}

export interface AgentDashboard {
  year: number;
  /** True when the caller is viewing a peer agent — goals, YTD, pending,
   *  closed_deals, and active_deals come back zeroed/empty in the response.
   *  Frontend uses this to show a "viewing public stats only" banner instead
   *  of misleading zero placeholders. */
  view_redacted?: boolean;
  user: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    roles: string[];
  };
  goals: AgentGoals | null;
  ytd: {
    units: number;
    volume_cents: number;
    /** Round L-9: GCI (gross commission income) — production credit
     *  from the closing-summary import. The headline "Earnings YTD". */
    gci_cents: number;
    /** Actual dollars disbursed per the DA (when applied) or GCI as
     *  fallback. Drives "Net paid YTD" + the variance number. */
    paid_cents: number;
    /** Backward-compat alias for gci_cents — read it on older builds. */
    earned_cents: number;
    /** Number of imported closing summaries that fed the YTD figures —
     *  used to hide the Earnings card when zero so we don't surface a
     *  misleading "$0 earned YTD". */
    earned_deals: number;
    /** Per-type closed counts (Round L-3, 2026-06-09). Drives the Deal
     *  mix tile + L:B ratio. */
    by_type: TypeBreakdown;
  };
  closed_deals: ClosedDeal[];
  /** Active + under-contract deals this agent is the primary on. Added so
   *  the production page can show pipeline alongside closed numbers. */
  active_deals: ActiveDeal[];
  activity: DailyActivity[];
  /** Under-contract totals — units + projected GCI from the pipeline. */
  pending: { units: number; volume_cents: number };
  /** Last 14 days vs the prior 14 — drives ▲/▼ deltas on activity KPIs. */
  period: {
    current: PeriodSums;
    prior: PeriodSums;
  };
}

export interface PeriodSums {
  dials: number;
  contacts: number;
  appts_set: number;
  appts_held: number;
  // R11-3 (Daniel 2026-08-12): listing appointments held, 14-day sums.
  listing_appts_held: number;
  listings_signed: number;
  buyers_signed: number;
  contracts: number;
  vendor_referrals: number;
  // Build 18: showings, 14-day sums.
  showings: number;
}

// ---------------------------------------------------------------------
// Top lead sources rollup — YTD by source with units + volume converted
// ---------------------------------------------------------------------
export interface LeadSourceRow {
  source: string;
  lead_count: number;
  converted_count: number;
  /** V-1 (Daniel 2026-08-10): null for callers who are not leadership. Volume
   *  by source is money, so it is owner / operations / admin only. */
  closed_volume_cents: number | null;
  pipeline_volume_cents: number | null;
}

export interface LeadSourcesResponse {
  sources: LeadSourceRow[];
  /** 'team' for leadership, 'own' when the counts cover only the caller's
   *  own leads. V-1 (Daniel 2026-08-10). */
  scope: 'team' | 'own';
  /** False when the two volume columns came back null. */
  money_visible: boolean;
}

export function getLeadSources(): Promise<LeadSourcesResponse> {
  return apiFetch('/api/production/lead-sources');
}

export function getTeamOverview(): Promise<TeamOverview> {
  return apiFetch('/api/production/team');
}

export interface ScoreboardAgent {
  id: number;
  first_name: string;
  last_name: string;
  goals: {
    /** V-2 (Daniel 2026-08-10): the two DAILY targets are activity, so they
     *  come back for every agent to every caller. They are the denominator of
     *  the streak badge and of the goal line on the sparkline. */
    daily_contacts: number;
    daily_calls: number;
    track_dials: boolean;
    /** V-2: annual targets are money. Null for anyone who is not owner,
     *  operations, or the agent themselves. Null rather than 0 so a client
     *  cannot print "0 of 12 units" against a real person. */
    unit_goal: number | null;
    annual_volume: number | null;
  };
  /** V-2: closed units and closed volume travel together as money. Both null
   *  when withheld. See `money_visible`. */
  ytd: { units: number | null; volume_cents: number | null };
  /** V-2 (Daniel 2026-08-10): true when this row's dollar fields carry a
   *  number rather than a null. Lets a card drop its money column instead of
   *  rendering dashes it then has to explain. */
  money_visible: boolean;
  this_week: ScoreboardBucket;
  this_month: ScoreboardBucket;
  /** Only present when the request included ?date=YYYY-MM-DD. Holds the
   *  selected day's totals for this agent — drives the "Specific day"
   *  mode of the period toggle. */
  this_day?: ScoreboardBucket;
  sparkline: number[]; // last 14 days of contacts_made (back-compat)
  /** L-126 (Daniel 2026-07-22): 14-day daily series per leaderboard metric,
   *  keyed by MetricKey ('dials','contacts','appointments_set', …). Lets the
   *  trend preview follow the selected metric. */
  sparklines?: Record<string, number[]>;
  streak: number;       // consecutive days hitting daily_contacts goal
  /** L-34 (Daniel 2026-06-25): yesterday snapshot for the team-wide
   *  "who logged?" panel. `logged=false` means no daily_activity row
   *  exists for that agent on yesterday's CT calendar day. Date is a
   *  noon-UTC unix anchor of yesterday's calendar day. */
  yesterday: {
    date: number;
    logged: boolean;
    calls: number;
    contacts_made: number;
    appointments_set: number;
    appointments_held: number;
    listings_signed: number;
    buyers_signed: number;
    contracts: number;
    /** R-4 (Daniel 2026-08-04): was fetched server-side but dropped from
     *  the payload, so the roster could never show referrals. */
    vendor_referrals: number;
  } | null;
}

export interface ScoreboardBucket {
  dials: number;
  contacts: number;
  appointments_set: number;
  appointments_held: number;
  listings_signed: number;
  buyers_signed: number;
  contracts: number;
  vendor_referrals: number;
}

export interface TeamRecord {
  user_id: number | null;
  first_name: string | null;
  last_name: string | null;
  date_unix: number | null;
  value: number;
}

export interface TeamRecords {
  best_day: TeamRecord;
  best_week: TeamRecord;
  best_month: TeamRecord;
  longest_streak: TeamRecord;
}

export interface Scoreboard {
  year: number;
  /** Which population the ACTIVITY numbers cover.
   *
   *  V-2 (Daniel 2026-08-10): 'me' is a view choice now, not a cage. Everyone
   *  defaults to 'team', and anyone may ask for 'me' to look at just their own
   *  numbers. What a caller may read is decided per field by the money gates,
   *  not by this. */
  scope: 'me' | 'team';
  /** V-2: true for everybody, because everybody can now look at the team's
   *  activity. Kept so an older bundle still knows to draw the toggle. */
  can_view_team: boolean;
  week_start: number;
  month_start: number;
  /** Unix seconds of the day the caller requested (UTC midnight). null
   *  when ?date wasn't provided. */
  selected_day?: number | null;
  team: {
    this_week: ScoreboardBucket;
    this_month: ScoreboardBucket;
    this_day?: ScoreboardBucket;
    /** V-2 (Daniel 2026-08-10): closed business is money, so these five are
     *  null unless the caller is owner or operations, or asked for scope=me
     *  and is therefore looking at their own numbers. Read
     *  `roster_money_visible` rather than testing one of them. */
    ytd_units: number | null;
    ytd_volume_cents: number | null;
    /** Investment business YTD (Daniel 2026-07-27, migration 0146) — the
     *  subset of ytd_units / ytd_volume_cents where the deal's buyer_type
     *  is 'investor'. Divide by the totals for "what % of my business is
     *  investment business", in units AND in volume. */
    investment_units: number | null;
    investment_volume_cents: number | null;
    /** Team-wide Listings · Auctions · Buyers split (Round L-3). */
    ytd_by_type: TypeBreakdown | null;
    /** V-2: whether the five closed-business fields above carry numbers. */
    roster_money_visible: boolean;
    /** Team-wide pay-to-office YTD — the KW Hometown Partners cut +
     *  royalty + BOLD etc., summed across every imported closed deal.
     *  Drives the Company Kept card on the Production overview.
     *
     *  V-1 (Daniel 2026-08-10): null for anyone who is not owner or
     *  operations. Null rather than 0 because 0 is a claim about the
     *  business and null is a statement about the reader. */
    company_kept_cents: number | null;
    /** Team-wide dollars paid to agents YTD — sums every payout row from
     *  imported closings. Daniel's "where does the money go" card.
     *  Null for non-owner/operations, same as company_kept_cents. */
    paid_to_agents_cents: number | null;
    /** Number of imported closing summaries this year. Used to hide the
     *  Company Kept / Paid to Agents cards when zero so the UI doesn't
     *  surface meaningless 0s before the first import. */
    imported_deals: number;
    /** V-1: true only for owner + operations. When false the two fields
     *  above are null and the "Where the money goes" card does not render:
     *  there is no per-agent version of "company kept" to fall back to. */
    team_money_visible: boolean;
  };
  records: TeamRecords;
  agents: ScoreboardAgent[];
}

export function getScoreboard(
  date?: string | null,
  scope?: 'me' | 'team',
): Promise<Scoreboard> {
  // `date` is YYYY-MM-DD; when present, the response includes per-agent
  // this_day buckets so the UI can render the Specific Day view.
  //
  // V-2 (Daniel 2026-08-10): `scope` is honoured for everybody now. It picks
  // which population the ACTIVITY numbers cover and nothing else. The dollar
  // figures are gated per field on the server no matter what is sent here.
  const qs = new URLSearchParams();
  if (date) qs.set('date', date);
  if (scope) qs.set('scope', scope);
  const q = qs.toString();
  return apiFetch(`/api/production/scoreboard${q ? `?${q}` : ''}`);
}

export function getAgentDashboard(userId: number, year?: number): Promise<AgentDashboard> {
  const yp = year ? `?year=${year}` : '';
  return apiFetch(`/api/production/agents/${userId}${yp}`);
}

// ─── Round L-11: Pipeline Power Score ──────────────────────────────────
export type PowerTier = 1 | 2 | 3 | 'bonus';

export interface PowerActivityProgress {
  key: string;
  label: string;
  tier: PowerTier;
  points: number;
  bundle_size: number;
  manual: boolean;
  completed: number;
  packs_completed: number;
  points_earned: number;
  description: string;
}

export interface PowerDailyScore {
  date_iso: string;
  total_points: number;
  tier_totals: { tier1: number; tier2: number; tier3: number; bonus: number };
  activities: PowerActivityProgress[];
}

export interface PowerScoreResponse {
  date_iso: string;
  user_id: number;
  daily_target: number;
  tier1_target: number;
  today: PowerDailyScore;
  seven_day_grid: Array<{
    date_iso: string;
    total_points: number;
    tier_totals: PowerDailyScore['tier_totals'];
  }>;
}

export function getPowerScore(opts?: { userId?: number; date?: string }): Promise<PowerScoreResponse> {
  const qs = new URLSearchParams();
  if (opts?.userId) qs.set('user_id', String(opts.userId));
  if (opts?.date) qs.set('date', opts.date);
  const suffix = qs.toString();
  return apiFetch(`/api/production/power-score${suffix ? `?${suffix}` : ''}`);
}

export function logPowerScoreManual(
  activityKey: string,
  count = 1,
): Promise<{ ok: true; today: PowerDailyScore }> {
  return apiFetch('/api/production/power-score/manual', {
    method: 'POST',
    body: JSON.stringify({ activity_key: activityKey, count }),
  });
}

export interface UpdateGoalsBody {
  year?: number;
  // Legacy targets
  annual_volume?: number;
  annual_units?: number;
  annual_appointments?: number;
  annual_agreements?: number;
  annual_listings?: number;
  annual_buyers?: number;
  daily_calls?: number;
  daily_contacts?: number;
  daily_leads?: number;
  daily_appointments?: number;
  daily_social_posts?: number;
  // MREA economic model
  unit_goal?: number;
  sell_side_pct?: number;
  working_weeks?: number;
  sell_avg_sale_price?: number;
  sell_commission_pct?: number;
  sell_sold_conversion?: number;
  sell_appointment_conversion?: number;
  buy_avg_sale_price?: number;
  buy_commission_pct?: number;
  buy_sold_conversion?: number;
  buy_appointment_conversion?: number;
  agent_team_split?: number;
  /** Per-agent listing-side split. Luis 0.50, Daniela 0.60. Buyer side uses
   *  the team-wide BUYER_TIER_SCHEDULE — no per-agent buyer split exists. */
  listing_split_pct?: number;
  conversations_per_appointment?: number;
  /** Per-agent toggle: show the Dials field in their daily activity logger. */
  track_dials?: boolean;
}

export function updateAgentGoals(userId: number, body: UpdateGoalsBody): Promise<{ ok: true }> {
  return apiFetch(`/api/production/agents/${userId}/goals`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

/**
 * R-4 (Daniel 2026-08-04): this is a PARTIAL patch. Send only the fields
 * you are changing — the endpoint now leaves every column you omit alone.
 * Do NOT re-send a whole cached row: that is what let a stale browser
 * cache overwrite a server-side vendor-referral credit, an ops edit, or a
 * second open tab, and what blanked `notes` on every +/- tap.
 */
export interface UpdateActivityBody {
  user_id?: number;
  date: number;                    // unix noon UTC of the CT calendar day
  calls?: number;                  // dials
  contacts_made?: number;
  leads_added?: number;
  appointments?: number;           // legacy — back-compat
  social_posts?: number;
  appointments_set?: number;
  appointments_held?: number;
  // R11-3 (Daniel 2026-08-12): listing appointments held, logged manually.
  listing_appointments_held?: number;
  listings_signed?: number;
  buyers_signed?: number;
  contracts?: number;
  vendor_referrals?: number;
  // Build 18: showings, logged manually.
  showings?: number;
  notes?: string;
}

export function updateActivity(body: UpdateActivityBody): Promise<{ ok: true }> {
  return apiFetch('/api/production/activity', {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

// ---------------------------------------------------------------------
// R11-4 (Daniel 2026-08-12): the coach scorecard. Monthly buckets in the
// KW production review shape. Net profit is always null on purpose; the
// row renders blank for Daniel and his coach to fill in.
// ---------------------------------------------------------------------
export interface ScorecardMonth {
  month: number; // 1..12
  appointments_set: number;
  appointments_held: number;
  listing_appointments_held: number;
  buyer_reps_signed: number;
  listings_taken: number;
  showings_scheduled: number;
  units_closed: number;
  volume_cents: number;
  gci_cents: number;
  net_profit_cents: null;
}

export interface CoachScorecard {
  year: number;
  scope: 'team' | 'agent';
  user_id: number | null;
  user: { id: number; first_name: string; last_name: string } | null;
  months: ScorecardMonth[];
  totals: Omit<ScorecardMonth, 'month'>;
}

export function getCoachScorecard(opts: {
  year: number;
  scope: 'team' | 'agent';
  userId?: number;
}): Promise<CoachScorecard> {
  const params = new URLSearchParams({ year: String(opts.year), scope: opts.scope });
  if (opts.scope === 'agent' && opts.userId != null) params.set('user_id', String(opts.userId));
  return apiFetch(`/api/production/scorecard?${params.toString()}`);
}

/** Fires the "log your numbers" reminder to ONLY the caller — preview test.
 *  `kind` picks which of the two daily emails to render:
 *    - 'morning'   → "Did you log yesterday's numbers?" (8am cron)
 *    - 'afternoon' → "Log today's numbers" (4:30pm cron, default)
 */
export interface TestReminderResult {
  sent: boolean;
  reason?: string;
  channels?: {
    sms?:   { attempted: boolean; ok: boolean; error?: string };
    email?: { attempted: boolean; ok: boolean; error?: string };
  };
}
export function sendTestReminderToSelf(
  kind: 'morning' | 'afternoon' = 'afternoon',
): Promise<TestReminderResult> {
  return apiFetch(`/api/production/send-update-reminder-self?kind=${kind}`, { method: 'POST' });
}

/**
 * Fire a preview of the Monday weekly digest at just the caller's email.
 * Subjects are prefixed "[Preview]" so the email is easy to spot.
 *
 * V-1 (Daniel 2026-08-10): what arrives depends on the caller. Owner and
 * operations get the team digest plus their own week-in-review, as before.
 * Everyone else gets the personal week-in-review only, because the team
 * digest is a leaderboard of names against closed volume. `email_sent`
 * refers to the TEAM digest, so it is false for an agent by design, and
 * `includes_team_digest` says which of the two calls this was.
 */
export function sendTestDigestToSelf(): Promise<{
  email_sent: boolean;
  email_reason?: string;
  personal_emails_sent: number;
  includes_team_digest: boolean;
}> {
  return apiFetch('/api/production/send-digest-self', { method: 'POST' });
}

/**
 * Build 79 (Daniel 2026-09-28): "add a send me a preview button" for the
 * month-end numbers email. Sends the email `userId` would get on `asOf`
 * (YYYY-MM-DD, a 1st of the month) to the CALLER only, with [Preview] in
 * the subject, copying nobody. The server decides who may look at whom:
 * leadership any agent, everyone else only themselves.
 */
export function sendAgentRecapPreview(args: { userId: number; asOf: string }): Promise<{
  ok: boolean;
  sent_to: string;
  periods: string[];
}> {
  const q = new URLSearchParams({ user_id: String(args.userId), as_of: args.asOf });
  return apiFetch(`/api/production/agent-recap/preview?${q.toString()}`, { method: 'POST' });
}

/**
 * Fire the daily activity reminder at the WHOLE TEAM right now — same
 * thing the 8am or 4:30pm cron would do. Owner/ops only. Confirm before
 * calling because this hits every active agent's phone/inbox.
 */
export interface TeamRemindersResult {
  team_id: number;
  sent: number;
  skipped: number;
  agents?: Array<{
    user_id: number;
    first_name: string;
    sms?:   { attempted: boolean; ok: boolean; error?: string };
    email?: { attempted: boolean; ok: boolean; error?: string };
  }>;
}
export function sendTeamReminders(
  kind: 'morning' | 'afternoon',
): Promise<TeamRemindersResult> {
  return apiFetch(`/api/production/send-update-reminders?kind=${kind}`, { method: 'POST' });
}

/**
 * Fire the weekly digest at the WHOLE TEAM right now — same thing the
 * Monday 9am cron would do. Owner/ops only. Sends the owner-style digest
 * to owner/ops and the personal week-in-review to every active agent.
 */
export function sendTeamDigest(): Promise<{
  team_id: number;
  email_sent: boolean;
  personal_emails_sent: number;
  email_reason?: string;
}> {
  return apiFetch('/api/production/send-digest', { method: 'POST' });
}

// ---------------------------------------------------------------------
// Team health board — Phase 4 (2026-07-05). Owner/operations/admin only.
// Dollar figures arrive in DOLLARS (server converts from cents).
// ---------------------------------------------------------------------
export interface HealthBoardAgent {
  id: number;
  name: string;
  active_deals: number;
  pipeline_value: number;
  ytd_closed_units: number;
  ytd_closed_volume: number;
  close_rate_pct: number | null;
  open_leads: number;
  days_since_activity: number | null;
  sphere_adds_month: number;
  open_commitments: number;
}

export function getHealthBoard(): Promise<{ year: number; agents: HealthBoardAgent[] }> {
  return apiFetch('/api/production/health-board');
}

// ---------------------------------------------------------------------
// C-3 (Daniel 2026-08-10): KW cap status.
//
// Two streams, tracked completely separately: company dollar to the Market
// Center and royalty to KWRI. They have their own caps, their own running
// totals, and their own cap YEARS, which for three of the four agents reset
// in different months. That is why this is an array rather than one object
// with two halves: the period_end on the two rows genuinely differs.
//
// All cents.
// ---------------------------------------------------------------------
export type CapStream = 'company_dollar' | 'royalty';

export interface CapStreamStatus {
  stream: CapStream;
  paid_cents: number;
  cap_cents: number;
  remaining_cents: number;
  pct: number;
  period_start: number;
  period_end: number;
  capped: boolean;
  capped_at: number | null;
}

export interface CapStatus {
  user_id: number;
  /** Null when this person has no agent_kw_settings row yet. */
  streams: CapStreamStatus[] | null;
  per_transaction_fee_cents: number;
  charges_per_transaction_fee: boolean;
  /** F-2 (Daniel 2026-08-13): the per-closing KW Cares deduction, or null
   *  when the agent has none. Sophia is the only agent with one today. */
  kw_cares_cents: number | null;
  kwuid: string | null;
}

/** Yourself always; anyone else needs owner or operations. A withheld answer
 *  is cap_status null with cap_status_visible false, never zeros. */
export function getAgentCapStatus(userId: number): Promise<{
  cap_status: CapStatus | null;
  cap_status_visible: boolean;
}> {
  return apiFetch(`/api/production/agents/${userId}/cap-status`);
}

export interface TeamCapStatusRow {
  user_id: number;
  first_name: string;
  last_name: string;
  cap_status: CapStatus;
}

/** The whole roster. Owner and operations only, enforced server side. */
export function getTeamCapStatus(): Promise<{ agents: TeamCapStatusRow[] }> {
  return apiFetch('/api/production/cap-status');
}

/** Human label for a stream. Used in both cap surfaces so they cannot
 *  disagree about what to call the two charges. */
export function capStreamLabel(stream: CapStream): string {
  return stream === 'royalty' ? 'Royalty (KWRI)' : 'Company dollar (Market Center)';
}
